/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import tw from 'twin.macro';

import { isEqual } from 'lodash';
import React from 'react';
import SettingsSuggest from 'assets/Icons/settingsSuggest.png';
import {
  DEFAULT_HIGH_LEVEL_PROCESSING_PROFILE,
  HighLevelProcessingProfileState,
  mergeHighLevelProcessingProfile,
  PreferenceOption,
  TenantProcessingProfileState,
} from 'utils/models';

export type {
  HighLevelProcessingProfileState,
  TenantProcessingProfileState,
} from 'utils/models';

export type ProcessingProfileEditAccess = {
  isAdministrator: boolean;
  isStandard: boolean;
  isEnterprise: boolean;
  isInternal: boolean;
};

export const canEditHighLevelProcessingProfile = ({
  isAdministrator,
  isStandard,
  isEnterprise,
  isInternal,
}: ProcessingProfileEditAccess): boolean =>
  isAdministrator && (isStandard || isEnterprise || isInternal);

export const applyHighLevelProcessingProfileSubmitGuard = (
  payload: PreferenceOption,
  savedPreference: PreferenceOption,
  canEdit: boolean,
): { payload: PreferenceOption; rejected: boolean } => {
  const savedProfile = mergeHighLevelProcessingProfile(savedPreference);
  const payloadProfile = mergeHighLevelProcessingProfile(payload);

  if (canEdit || isEqual(savedProfile, payloadProfile)) {
    return { payload, rejected: false };
  }

  return {
    payload: {
      ...payload,
      highLevelProcessingProfile: savedProfile,
    },
    rejected: true,
  };
};

export interface ProcessingProfileOptionsProps {
  state: HighLevelProcessingProfileState;
  onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  variant?: 'profile' | 'preferences';
}

export const defaultProcessingProfileSettings: TenantProcessingProfileState = {
  highLevelProcessingProfile: { ...DEFAULT_HIGH_LEVEL_PROCESSING_PROFILE },
};

type CheckboxOption = {
  name: keyof HighLevelProcessingProfileState;
  label: string;
};

const aiProcessingOptions: CheckboxOption[] = [{ name: 'iab', label: 'IAB' }];

const transcriptionAccessOptions: CheckboxOption[] = [
  { name: 'entities', label: 'Entities' },
  {
    name: 'chapterisation',
    label: 'Chapterisation & Paragraph Mode',
  },
  { name: 'waveform', label: 'Waveform' },
];

export const ProcessingProfileOptions = ({
  state,
  onCheckboxChange,
  variant = 'profile',
}: ProcessingProfileOptionsProps) => {
  const isPreferences = variant === 'preferences';

  const renderCheckbox = ({ name, label }: CheckboxOption) => (
    <div key={`${name}-${label}`} tw="flex">
      <label
        className={isPreferences ? 'large-10 cell' : 'cell cus-inline-block'}
        css={
          isPreferences
            ? tw`ml-20 max-w-full flex[unset]! w-auto cursor-pointer`
            : tw`mb-2 ml-14 w-auto cursor-pointer`
        }
      >
        <input
          name={name}
          checked={state[name]}
          onChange={onCheckboxChange}
          type="checkbox"
        />
        <span
          className="checkmark"
          style={{ top: isPreferences ? '5px' : '4px' }}
        />
        {isPreferences ? label : <span>{label}</span>}
      </label>
    </div>
  );

  const renderSection = (title: string, options: CheckboxOption[]) => {
    const content = (
      <>
        <div className="prefTitle" tw="flex items-center space-x-2">
          <img src={SettingsSuggest} alt="Settings Suggest" css={iconStyle} />
          <h3 tw="mb-0">{title}</h3>
        </div>
        {options.map(renderCheckbox)}
      </>
    );

    if (isPreferences) {
      return (
        <div className="cell">
          <div>{content}</div>
        </div>
      );
    }

    return <div>{content}</div>;
  };

  return (
    <div css={[tw`flex flex-col`, !isPreferences && tw`space-y-6`]}>
      {renderSection('AI processing configuration', aiProcessingOptions)}
      {renderSection(
        'Transcription access configuration',
        transcriptionAccessOptions,
      )}
    </div>
  );
};

const iconStyle = css`
  width: 40px;
`;
